import {
    AddOutlined,
    CampaignOutlined,
    ChevronRightOutlined,
    DashboardOutlined,
    DescriptionOutlined,
    Inventory2Outlined,
    LogoutOutlined,
    PaymentsOutlined,
    PeopleAltOutlined,
    QuestionAnswerOutlined,
    ReceiptLongOutlined,
    SellOutlined,
    ShoppingCartOutlined,
} from '@mui/icons-material';
import { DocumentType } from '@synergy/models';
import { Notifier } from '@synergy/shared/snackbar/feature';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    PALO_STATUS_NAV_LABEL,
    PaloChrome,
    PaloCommand,
    PaloCommandGroup,
    PaloCommandPalette,
    PaloNavSubGroup,
    PaloSection,
    PaloShortcutOverlay,
    PaloStatusScaleProvider,
    paloInitials,
    paloSignOutHref,
    usePaloShortcut,
    usePaloShortcutList,
} from '@synergy/components-palo';
import { ReactElement, useCallback, useRef, useState } from 'react';
import { getUrlSegment } from '@synergy/utils';

import { OmniSearchHit, OmniSearchMasterDataHit } from './common/api/omni-search-api';
import { PALO_FRESH_LIST } from './common/components/palo/use-palo-list-views';
import { ROUTES } from './features/sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from './store/use-appstate-selector';
import { usePaloNavCounts } from './palo-nav-counts';
import { useThunkDispatch } from './store/use-thunk-dispatch';
import paloLogo from './common/style/pool-alpin-logo.svg';

/** What a hit is called in the palette. */
const DOCUMENT_LABEL: Partial<Record<DocumentType, string>> = {
    [DocumentType.PurchaseOrder]: 'Bestellung',
    [DocumentType.Invoice]: 'Rechnung',
    [DocumentType.Inquiry]: 'Anfrage',
    [DocumentType.SalesOrder]: 'Auftrag',
    [DocumentType.Demand]: 'Bedarfserhebung',
};

/** Real, server-supported document status sub-views (filter[status]=<DocumentStatus>). */
/**
 * The sub-views of a document kind.
 *
 * They used to be the same five status constants for every kind, which said
 * more about the enum than about the work: an inquiry never carries
 * STATUS_COMPLETED, so its "Abgeschlossen" stood at nought while 30.830 were
 * finished, and 82.980 purchase orders ended without an invoice with nowhere to
 * find them.
 *
 * The study names them after what someone is looking for instead, and the
 * grouping follows: what is still on the table above, what is filed away below.
 * An order that ended without an invoice is the clearest case — it belongs to
 * the work, not to the archive, and it is marked as something to look at.
 */
/**
 * One status keeps the plain form every link and saved view already carries;
 * several use the list form, which is the only one that works — repeating the
 * plain key makes the endpoint answer on the last value alone.
 */
const byStatus = (base: string, ...statuses: string[]): string =>
    1 === statuses.length
        ? `${base}?filter[status]=${statuses[0]}`
        : `${base}?${statuses.map(status => `filter[status][]=${status}`).join('&')}`;

const demandGroups = (base: string): PaloNavSubGroup[] => [
    {
        label: 'Aktuell',
        items: [
            { label: 'Alle', to: base },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CREATED'], to: byStatus(base, 'STATUS_CREATED') },
            { label: PALO_STATUS_NAV_LABEL['STATUS_ONGOING'], to: byStatus(base, 'STATUS_ONGOING') },
        ],
    },
    {
        label: 'Archiv',
        items: [{ label: PALO_STATUS_NAV_LABEL['STATUS_COMPLETED'], to: byStatus(base, 'STATUS_COMPLETED') }],
    },
];

const inquiryGroups = (base: string): PaloNavSubGroup[] => [
    {
        label: 'Laufend',
        items: [
            { label: 'Alle', to: base },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CREATED'], to: byStatus(base, 'STATUS_CREATED') },
            { label: PALO_STATUS_NAV_LABEL['STATUS_ONGOING'], to: byStatus(base, 'STATUS_ONGOING') },
            // Running for 21 days or longer; the server holds the rule (#2260).
            { label: 'Seit 21 Tagen offen', to: `${base}?filter[longOpen]=1`, alert: true },
        ],
    },
    {
        label: 'Archiv',
        // One entry over both: an inquiry that ended is done whether or not a
        // document followed, and the study names it once.
        items: [
            {
                label: 'Abgeschlossen',
                to: byStatus(base, 'STATUS_COMPLETED_WITH_TRANSITION', 'STATUS_COMPLETED_WITHOUT_TRANSITION'),
            },
        ],
    },
];

const purchaseOrderGroups = (base: string): PaloNavSubGroup[] => [
    {
        label: 'Aktuell',
        items: [
            { label: 'Alle', to: base },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CREATED'], to: byStatus(base, 'STATUS_CREATED') },
            // The study's "Ohne Rechnung": orders of charging suppliers that have
            // no invoice 35 days after they were completed — the invoicing's
            // work list (#2264). A direct supplier's order never lands here.
            {
                label: 'Ohne Rechnung',
                to: byStatus(base, 'STATUS_COMPLETED_WITHOUT_TRANSITION'),
                alert: true,
            },
        ],
    },
    {
        label: 'Archiv',
        items: [
            { label: 'Abgerechnet', to: byStatus(base, 'STATUS_COMPLETED_WITH_TRANSITION') },
            { label: PALO_STATUS_NAV_LABEL['STATUS_COMPLETED'], to: byStatus(base, 'STATUS_COMPLETED') },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CANCELED'], to: byStatus(base, 'STATUS_CANCELED') },
        ],
    },
];

const salesOrderGroups = (base: string): PaloNavSubGroup[] => [
    {
        label: 'Aktuell',
        items: [
            { label: 'Alle', to: base },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CREATED'], to: byStatus(base, 'STATUS_CREATED') },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CART_PENDING'], to: byStatus(base, 'STATUS_CART_PENDING') },
        ],
    },
    {
        label: 'Archiv',
        items: [{ label: PALO_STATUS_NAV_LABEL['STATUS_COMPLETED'], to: byStatus(base, 'STATUS_COMPLETED') }],
    },
];

const invoiceGroups = (base: string): PaloNavSubGroup[] => [
    {
        label: 'Eingang',
        items: [
            { label: 'Alle', to: base },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CREATED'], to: byStatus(base, 'STATUS_CREATED') },
        ],
    },
    {
        label: 'Abgeschlossen',
        items: [
            { label: PALO_STATUS_NAV_LABEL['STATUS_COMPLETED'], to: byStatus(base, 'STATUS_COMPLETED') },
            { label: PALO_STATUS_NAV_LABEL['STATUS_CANCELED'], to: byStatus(base, 'STATUS_CANCELED') },
            // A credit note is a completed invoice with the flag set, so this
            // narrows the entry above rather than standing beside it (#2184).
            { label: 'Gutschrift', to: `${byStatus(base, 'STATUS_COMPLETED')}&filter[isCredit]=true` },
        ],
    },
];

const sections: PaloSection[] = [
    {
        key: 'cockpit',
        label: 'Cockpit',
        icon: <DashboardOutlined />,
        to: ROUTES.dashboard,
        navTitle: 'Cockpit',
        groups: [{ label: 'Mein Arbeitstag', items: [{ label: 'Übersicht', to: ROUTES.dashboard }] }],
    },
    {
        key: 'demands',
        label: 'Bedarfserhebungen',
        icon: <DescriptionOutlined />,
        to: ROUTES.demands,
        navTitle: 'Bedarfserhebungen',
        groups: demandGroups(ROUTES.demands),
    },
    {
        key: 'inquiries',
        label: 'Anfragen',
        icon: <QuestionAnswerOutlined />,
        to: ROUTES.inquiries,
        navTitle: 'Anfragen',
        groups: inquiryGroups(ROUTES.inquiries),
    },
    {
        key: 'purchaseOrders',
        label: 'Bestellungen',
        icon: <ShoppingCartOutlined />,
        to: ROUTES.purchaseOrders,
        navTitle: 'Bestellungen',
        groups: purchaseOrderGroups(ROUTES.purchaseOrders),
    },
    {
        // Named by the document like every other module; the order is an
        // "Auftrag" in the interface (GF 30.09.2026, #2216).
        key: 'salesOrders',
        label: 'Aufträge',
        icon: <SellOutlined />,
        to: ROUTES.salesOrders,
        navTitle: 'Aufträge',
        groups: salesOrderGroups(ROUTES.salesOrders),
    },
    {
        key: 'invoices',
        label: 'Rechnungen',
        icon: <ReceiptLongOutlined />,
        to: ROUTES.invoices.list,
        navTitle: 'Rechnungen',
        groups: invoiceGroups(ROUTES.invoices.list),
    },
    {
        key: 'bonusSettlements',
        label: 'Bonusabrechnungen',
        icon: <PaymentsOutlined />,
        to: ROUTES.bonusSettlements.overview,
        navTitle: 'Bonusabrechnungen',
        groups: [{ label: 'Übersicht', items: [{ label: 'Alle Abrechnungen', to: ROUTES.bonusSettlements.overview }] }],
    },
    {
        key: 'content',
        label: 'Inhalte',
        icon: <CampaignOutlined />,
        to: ROUTES.contentManagement.headerBanner.list,
        navTitle: 'Inhalte',
        groups: [
            {
                label: 'Banner',
                items: [
                    { label: 'Header-Banner', to: ROUTES.contentManagement.headerBanner.list },
                    { label: 'Sponsor-Banner', to: ROUTES.contentManagement.sponsorBanner.list },
                ],
            },
            {
                label: 'Redaktion',
                items: [
                    { label: 'Pool-News', to: ROUTES.contentManagement.poolNews.list },
                    { label: 'Statische Seiten', to: ROUTES.contentManagement.staticSites.list },
                ],
            },
            {
                label: 'Angebote',
                items: [{ label: 'Sonderangebote', to: ROUTES.contentManagement.supplierSpecialOffers.list }],
            },
            {
                label: 'Einstellungen',
                items: [{ label: 'Footer', to: ROUTES.contentManagement.footer }],
            },
        ],
    },
    {
        key: 'masterData',
        label: 'Stammdaten',
        icon: <PeopleAltOutlined />,
        to: ROUTES.contactsAndOrganizations.contacts.list,
        navTitle: 'Stammdaten',
        groups: [
            {
                label: 'Verzeichnis',
                items: [
                    { label: 'Personen', to: ROUTES.contactsAndOrganizations.contacts.list },
                    { label: 'Lieferanten', to: ROUTES.contactsAndOrganizations.suppliers.list },
                    { label: 'Mitglieder', to: ROUTES.contactsAndOrganizations.customers.list },
                ],
            },
            {
                label: 'Katalog',
                items: [
                    { label: 'Artikel', to: ROUTES.products.product.base },
                    { label: 'Import', to: ROUTES.products.import.overview.all },
                    { label: 'Standard-Mapping', to: ROUTES.products.mapping.base },
                    { label: 'Konfiguration', to: ROUTES.configuration.data },
                    { label: 'Suchqualität', to: ROUTES.configuration.search },
                ],
            },
        ],
    },
];

/** Pathname prefixes that map a route onto its rail section. */
const sectionMatchers: { key: string; prefixes: string[] }[] = [
    { key: 'cockpit', prefixes: [ROUTES.dashboard] },
    { key: 'demands', prefixes: [ROUTES.demands] },
    { key: 'inquiries', prefixes: [ROUTES.inquiries] },
    { key: 'purchaseOrders', prefixes: [ROUTES.purchaseOrders] },
    { key: 'salesOrders', prefixes: [ROUTES.salesOrders] },
    { key: 'invoices', prefixes: [ROUTES.invoices.list] },
    { key: 'bonusSettlements', prefixes: [ROUTES.bonusSettlements.overview] },
    {
        key: 'content',
        prefixes: [
            ROUTES.contentManagement.headerBanner.list,
            ROUTES.contentManagement.sponsorBanner.list,
            ROUTES.contentManagement.poolNews.list,
            ROUTES.contentManagement.staticSites.list,
            ROUTES.contentManagement.supplierSpecialOffers.list,
            ROUTES.contentManagement.footer,
        ],
    },
    {
        key: 'masterData',
        prefixes: [
            ROUTES.contactsAndOrganizations.contacts.list,
            ROUTES.contactsAndOrganizations.suppliers.list,
            ROUTES.contactsAndOrganizations.customers.list,
            ROUTES.products.product.base,
            ROUTES.products.import.overview.base,
            ROUTES.products.mapping.base,
            ROUTES.configuration.data,
            ROUTES.configuration.finance,
            ROUTES.configuration.organization,
            ROUTES.configuration.search,
        ],
    },
];

const resolveSectionKey = (pathname: string): string => {
    const match = sectionMatchers.find(m => m.prefixes.some(p => pathname === p || pathname.startsWith(p + '/')));

    return match?.key ?? 'cockpit';
};

export const PaloAppLayout = (): ReactElement => {
    const location = useLocation();
    const navigate = useNavigate();
    const [commandOpen, setCommandOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [hits, setHits] = useState<OmniSearchHit[]>([]);
    const [masterData, setMasterData] = useState<OmniSearchMasterDataHit[]>([]);
    const [searching, setSearching] = useState(false);
    const searchRef = useRef(0);
    const dispatch = useThunkDispatch();

    const handleQueryChange = useCallback(
        async (term: string) => {
            const requestId = ++searchRef.current;

            if (term.length < 2) {
                setHits([]);
                setMasterData([]);
                setSearching(false);

                return;
            }

            setSearching(true);

            const result = await dispatch((_d, _g, { api }) => api.omniSearch.search(term));

            // A slower answer to an older query must not overwrite a newer one.
            if (requestId !== searchRef.current) {
                return;
            }

            setHits(result.documents);
            setMasterData(result.masterData);
            setSearching(false);
        },
        [dispatch]
    );
    const shortcuts = usePaloShortcutList();

    // The topbar's ⌘K hint used to be answered by a hand-written listener of
    // its own. It goes through the registry now, so it shows up under "?" and
    // cannot quietly exist twice — which is what made it stop working.
    usePaloShortcut({
        combo: 'k',
        meta: true,
        group: 'Allgemein',
        label: 'Kommandopalette öffnen',
        // Opens, never toggles: a toggle is invisible if the key ever
        // arrives twice, and Esc is what closes the palette.
        handler: () => setCommandOpen(true),
    });

    usePaloShortcut({
        combo: '?',
        group: 'Allgemein',
        label: 'Tastenkürzel anzeigen',
        handler: () => setShortcutsOpen(true),
    });
    const statusScale = useAppSelector(state => state.settings.statusScale);
    const fullName = useAppSelector(state => state.user?.fullName ?? '');
    const isImpersonator = useAppSelector(state => !!state.settings.isImpersonator);

    const activeSectionKey = resolveSectionKey(location.pathname);
    const activeSection = sections.find(s => s.key === activeSectionKey);
    const { counts, invalid } = usePaloNavCounts(activeSection);

    // A command that leads somewhere carries the target as a link too, so it
    // opens in a new tab on Ctrl/⌘ or the middle button (#2327).
    const goTo = (to: string): Pick<PaloCommand, 'run' | 'href'> => ({ href: to, run: () => navigate(to) });

    const commandGroups: PaloCommandGroup[] = [
        ...(hits.length > 0
            ? [
                  {
                      label: 'Belege',
                      // Already answered by the search engine; the palette must
                      // not sieve them through its own label matching.
                      preFiltered: true,
                      items: hits.map(hit => ({
                          label: `${DOCUMENT_LABEL[hit.documentType] ?? ''} ${hit.number}`.trim(),
                          sub: hit.party,
                          icon: <DescriptionOutlined />,
                          ...goTo(`/${getUrlSegment(hit.documentType)}/${hit.id}`),
                      })),
                  },
              ]
            : []),
        ...(masterData.length > 0
            ? [
                  {
                      label: 'Stammdaten',
                      preFiltered: true,
                      items: masterData.map(hit => ({
                          label: hit.label,
                          sub: hit.sub,
                          icon: <PeopleAltOutlined />,
                          ...goTo(
                              {
                                  customer: ROUTES.contactsAndOrganizations.customers.detail,
                                  supplier: ROUTES.contactsAndOrganizations.suppliers.detail,
                                  contact: ROUTES.contactsAndOrganizations.contacts.detail,
                              }[hit.kind].replace(':id', String(hit.id))
                          ),
                      })),
                  },
              ]
            : []),
        {
            label: 'Springen zu',
            items: sections.flatMap(section => [
                { label: section.label, sub: 'Bereich', icon: section.icon, ...goTo(section.to) },
                ...section.groups
                    .flatMap(group => group.items)
                    .filter(item => item.to !== section.to)
                    .map(item => ({
                        label: `${section.label}: ${item.label}`,
                        sub: section.navTitle,
                        icon: <ChevronRightOutlined />,
                        ...goTo(item.to),
                    })),
            ]),
        },
        {
            label: 'Aktionen',
            items: [
                {
                    label: 'Neuer Kontakt',
                    sub: 'Anlegen',
                    icon: <AddOutlined />,
                    ...goTo(ROUTES.contactsAndOrganizations.contacts.new),
                },
                {
                    label: 'Neues Mitglied',
                    sub: 'Anlegen',
                    icon: <AddOutlined />,
                    ...goTo(ROUTES.contactsAndOrganizations.customers.new),
                },
                {
                    label: 'Neuer Lieferant',
                    sub: 'Anlegen',
                    icon: <AddOutlined />,
                    ...goTo(ROUTES.contactsAndOrganizations.suppliers.new),
                },
                {
                    label: 'Neues Produkt',
                    sub: 'Anlegen',
                    icon: <AddOutlined />,
                    ...goTo(ROUTES.products.product.new),
                },
            ],
        },
    ];

    const sectionsWithCounts = sections.map(section =>
        section.key === activeSectionKey
            ? {
                  ...section,
                  groups: section.groups
                      .map(group => ({
                          ...group,
                          items: group.items
                              .filter(item => !invalid.has(item.to))
                              .map(item =>
                                  counts[item.to] !== undefined ? { ...item, count: counts[item.to] } : item
                              ),
                      }))
                      .filter(group => group.items.length > 0),
              }
            : section
    );

    return (
        <PaloStatusScaleProvider scale={statusScale}>
            <PaloChrome
                sections={sectionsWithCounts}
                activeSectionKey={activeSectionKey}
                activePath={location.pathname + location.search}
                // A list opened from the navigation starts without the search
                // left behind days ago; its filters stay (R-271).
                onNavigate={to => navigate(to, { state: { [PALO_FRESH_LIST]: true } })}
                onSearchClick={() => setCommandOpen(true)}
                onSettingsClick={() => navigate(ROUTES.settings)}
                userName={fullName}
                userInitials={paloInitials(fullName)}
                userMenuItems={[
                    {
                        // The wording the drawer carried before the redesign,
                        // and what the portals show from the shared messages.
                        label: isImpersonator ? 'Logout (Imp.)' : 'Logout',
                        href: paloSignOutHref(isImpersonator),
                        icon: <LogoutOutlined />,
                    },
                ]}
                logo={<img src={paloLogo} alt="PALO" style={{ width: 124, height: 'auto', display: 'block' }} />}
            >
                <Outlet />
            </PaloChrome>
            <PaloCommandPalette
                open={commandOpen}
                onClose={() => setCommandOpen(false)}
                groups={commandGroups}
                contextLabel={activeSection?.label}
                onQueryChange={handleQueryChange}
                searching={searching}
            />
            <PaloShortcutOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} shortcuts={shortcuts} />
            <Notifier />
        </PaloStatusScaleProvider>
    );
};
